import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, orderBy, doc, setDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { UserPlus, Trash2, Search, X, Mail, Phone } from 'lucide-react';
import { db } from '@/lib/firebase';
import type { Client } from '@/lib/types';

const blank = (): Omit<Client, 'id' | 'createdAt' | 'updatedAt'> => ({ fullName: '', email: '', phone: '', notes: '', visits: 0 });

export function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Client | null>(null);
  const [form, setForm] = useState(blank());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'clients'), orderBy('fullName'));
    return onSnapshot(
      q,
      (snap) => setClients(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Client, 'id'>) }))),
      () => setClients([]),
    );
  }, []);

  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c) =>
      [c.fullName, c.email, c.phone].some((v) => (v || '').toLowerCase().includes(s)),
    );
  }, [clients, search]);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3000); }

  function openNew() { setEditing(null); setForm(blank()); setOpen(true); }
  function openEdit(c: Client) {
    setEditing(c);
    setForm({ fullName: c.fullName, email: c.email || '', phone: c.phone || '', notes: c.notes || '', visits: c.visits || 0 });
    setOpen(true);
  }

  async function save() {
    if (!form.fullName.trim()) return flash('A name is required.');
    setBusy(true);
    const now = new Date().toISOString();
    try {
      if (editing) {
        await setDoc(doc(db, 'clients', editing.id), { ...form, updatedAt: now }, { merge: true });
      } else {
        await addDoc(collection(db, 'clients'), { ...form, createdAt: now, updatedAt: now });
      }
      setOpen(false);
      flash('Saved.');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: Client) {
    setBusy(true);
    try { await deleteDoc(doc(db, 'clients', c.id)); flash('Removed.'); }
    catch (e) { flash(e instanceof Error ? e.message : 'Delete failed.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-espresso">Clients</h1>
          <p className="mt-1 font-body text-sm text-muted">Your guest directory and notes.</p>
        </div>
        <button onClick={openNew} className="btn-primary !px-5 !py-2.5"><UserPlus size={15} /> Add client</button>
      </div>

      <div className="mt-6 relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input className="field !pl-9" placeholder="Search by name, email, phone" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="mt-5 overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[560px] text-left">
          <thead>
            <tr className="border-b border-line font-body text-[11px] uppercase tracking-wide text-muted">
              <th className="px-5 py-3">Name</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Visits</th><th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id} className="border-b border-line last:border-0 hover:bg-cream/40">
                <td className="px-5 py-3.5">
                  <button onClick={() => openEdit(c)} className="font-body text-sm font-semibold text-espresso hover:text-gold-deep">{c.fullName}</button>
                  {c.notes && <p className="truncate font-body text-xs text-muted">{c.notes}</p>}
                </td>
                <td className="px-5 py-3.5 font-body text-xs text-muted">
                  {c.email && <span className="block">{c.email}</span>}
                  {c.phone && <span className="block">{c.phone}</span>}
                </td>
                <td className="px-5 py-3.5 font-display text-base text-espresso">{c.visits || 0}</td>
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => remove(c)} disabled={busy} className="text-muted hover:text-blush-deep" aria-label="Remove"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center font-body text-sm text-muted">No clients yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-cream p-7 shadow-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-espresso">{editing ? 'Edit client' : 'New client'}</h2>
              <button onClick={() => setOpen(false)} className="text-muted hover:text-espresso"><X size={20} /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div><label className="label">Full name</label><input className="field" value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Email</label><div className="relative"><Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" /><input className="field !pl-9" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div></div>
                <div><label className="label">Phone</label><div className="relative"><Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" /><input className="field !pl-9" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div></div>
              </div>
              <div><label className="label">Visits</label><input type="number" className="field" value={form.visits} onChange={(e) => setForm((f) => ({ ...f, visits: Number(e.target.value) }))} /></div>
              <div><label className="label">Notes</label><textarea rows={3} className="field" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Preferences, allergies, history…" /></div>
              <button onClick={save} disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Save client'}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
